/* eslint-disable max-len */
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { closeIcon } from "../../utils/svg";
import styles from "./Modal.module.scss";

interface ModalProps {
  title: string;
  onClose: (outsideClick: boolean) => void;
  options?: ModalOptions;
  children?: ReactNode | undefined;
}

export interface GenericModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

export const ModalComponent: React.FC<ModalProps> = ({
  title,
  onClose,
  options,
  children,
}: ModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const onDocumentClick = useCallback(
    (event: Event) => {
      if (
        contentRef.current &&
        event.target &&
        event.target instanceof Element &&
        !contentRef.current.contains(event.target) &&
        !options?.persistent
      ) {
        onClose(true);
      }
    },
    [contentRef, onClose, options],
  );

  useEffect(() => {
    setTimeout(() => {
      document.addEventListener("click", onDocumentClick);
    });

    return (): void => {
      document.removeEventListener("click", onDocumentClick);
    };
  }, [onDocumentClick, onClose]);

  useEffect((): (() => void) => {
    document.addEventListener("keydown", onKeyDown);
    return (): void => document.removeEventListener("keydown", onKeyDown);
  });

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!options?.persistent) {
      event.key === "Escape" && onClose(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={styles["modal"]}>
      <div className={styles["modal-content"]} ref={contentRef}>
        <div className={styles["modal-header"]}>
          <h2>{title}</h2>
          <button
            className={styles["close-button"]}
            onClick={() => onClose(false)}>
            <svg viewBox="0 0 26 27" fill="currentColor" width="20" height="20">
              <path d={closeIcon} />
            </svg>
          </button>
        </div>
        <div className={styles["modal-body"]}>{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export type ModalOptions = Partial<{
  persistent: boolean;
  onClose: () => void;
  onSuccess: () => void;
  disableOutsideClick: boolean;
}>;

interface ModalContextValue {
  registerModal: (
    uuid: string,
    node: React.ReactNode,
    title: string,
    options?: ModalOptions,
  ) => void;
  showModal: (
    uuid: string,
    node: React.ReactNode,
    title: string,
    options?: ModalOptions,
  ) => void;
  closeModal: (uuid: string) => void;
  isOpen: (uuid: string) => boolean;
}

interface ModalConfig {
  node: React.ReactNode;
  title: string;
  options?: Partial<ModalOptions>;
  isOpen?: boolean;
}

const ModalContext = React.createContext<ModalContextValue>({
  registerModal: () => {
    void 0;
  },
  closeModal: () => {
    void 0;
  },
  isOpen: () => false,
  showModal: () => {
    void 0;
  },
});

type ModalProviderProps = {
  children?: React.ReactNode | undefined;
};

export const ModalProvider: React.FC<{ children: React.ReactNode }> = (
  props: ModalProviderProps,
) => {
  const [modalList, setModalList] = useState<{ [key: string]: ModalConfig }>(
    {},
  );

  const registerModal = (
    uuid: string,
    node: ReactNode,
    title: string,
    options?: ModalOptions,
  ): void => {
    if (!modalList[uuid]) {
      setModalList((modals) => ({
        ...modals,
        [uuid]: { isOpen: false, node, options: options || {}, title },
      }));
    }
  };

  const showModal = (
    uuid: string,
    node: ReactNode,
    title: string,
    options?: ModalOptions,
  ): void => {
    if (!modalList[uuid]) {
      setModalList((modals) => ({
        ...modals,
        [uuid]: { isOpen: false, node, options: options || {}, title },
      }));
    }

    if (!modalList[uuid]?.isOpen) {
      setModalList((modals) => ({
        ...modals,
        [uuid]: { ...modals[uuid]!, isOpen: true },
      }));
    }
  };

  const closeModal = (uuid: string, callOnClose = true): void => {
    const modal = modalList[uuid];
    if (modal) {
      setModalList((modals) => ({
        ...modals,
        [uuid]: { ...modals[uuid]!, isOpen: false },
      }));
      if (modal.options?.onClose && callOnClose) {
        modal.options.onClose();
      }
    }
  };

  const isOpen = (uuid: string): boolean => {
    return (modalList[uuid] !== undefined && modalList[uuid]?.isOpen) || false;
  };

  const onSuccess = (uuid: string): void => {
    const modal = modalList[uuid];
    closeModal(uuid, false);
    if (modal && modal.isOpen && modal.options?.onSuccess) {
      modal.options.onSuccess();
    }
  };

  return (
    <ModalContext.Provider
      value={{
        closeModal,
        isOpen,
        showModal,
        registerModal,
      }}>
      {props.children}
      {Object.keys(modalList)
        .filter((uuid) => modalList[uuid]?.isOpen)
        .map((uuid) => {
          const node = modalList[uuid]!.node;

          return (
            <ModalComponent
              key={`modal-${uuid}`}
              options={modalList[uuid]!.options}
              onClose={(outsideClick): void => {
                if (
                  modalList[uuid]?.options?.disableOutsideClick &&
                  outsideClick
                ) {
                  return;
                }
                closeModal(uuid);
              }}
              title={modalList[uuid]!.title}>
              {React.isValidElement(node) &&
                React.cloneElement(node as React.ReactElement<ModalOptions>, {
                  onClose: () => {
                    closeModal(uuid);
                  },
                  onSuccess: () => {
                    onSuccess(uuid);
                  },
                })}
            </ModalComponent>
          );
        })}
    </ModalContext.Provider>
  );
};

export interface ModalHook {
  showModal: () => void;
  closeModal: () => void;
  isOpen: boolean;
}

export const useModal = (
  title: string,
  node: ReactNode,
  options?: ModalOptions,
): ModalHook => {
  const { registerModal, showModal, closeModal, isOpen } =
    useContext(ModalContext);
  const ref = useRef<ReactNode>(node);

  useEffect(() => {
    registerModal(title, node, title, options);
  }, [title, registerModal, node, options]);

  useEffect(() => {
    ref.current = node;
  }, [node]);

  const presentModal = useCallback(() => {
    showModal(title, ref.current, title, options);
  }, [title, ref, showModal, options]);

  const unpresentModal = useCallback(() => {
    closeModal(title);
  }, [closeModal, title]);

  const isModalOpen = useMemo(() => isOpen(title), [isOpen, title]);

  return {
    closeModal: unpresentModal,
    isOpen: isModalOpen,
    showModal: presentModal,
  };
};
